import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Progress,
  Tag,
  Divider,
  Modal,
  Input,
  Select,
  Table,
  ConfigProvider,
  Avatar,
  message,
} from "antd";
import {
  ClockCircleOutlined,
  DownOutlined,
  InfoCircleOutlined,
  RightOutlined,
  UpOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import Text from "../../components/Text/Text";
import { color, icon, image } from "../../resource";
import { FlexCol, FlexRow } from "../../components/Container/Container";
import Button from "../../components/Button/Button";
import { useNavigate } from "react-router";
import {
  exportFlashSale,
  flashSaleSSELiveUrl,
  getFlashSaleTracking,
  getFlashSaleTrackingCart,
} from "../../datasource/FlashSaleDatasource";
import { useSSEFlashSale } from "../../hook/useSSE";
import dayjs from "dayjs";
import {
  mapBgColorRound,
  mapQuotaType,
  mapStatusRound,
  mapTextColorRound,
} from "../../definitions/flashSaleStatus";
import Timer from "antd/es/statistic/Timer";
import { numberFormatter } from "../../utility/Formatter";
import { useEffectOnce } from "react-use";
import { useNotificationContext } from "../../context/NotificationProvider";
import Permission from "../../components/Permission/Permission";

const StyledCard = styled(Card)<{ backgroundColor?: string[] }>`
  border: none;
  border-radius: 12px;
  overflow: hidden;

  background: ${({ backgroundColor }) =>
    backgroundColor
      ? `linear-gradient(90deg, ${backgroundColor[0]} 0%, ${backgroundColor[1]} 50%, ${backgroundColor[2]} 100%)`
      : "linear-gradient(90deg, #f8f8f7 0%, #ffffff 50%, #f8ecd8 100%)"};

  .ant-card-body {
    padding: 18px 20px;
  }
`;

const StyledTimer = styled(Timer)`
  .ant-statistic-content {
    font-weight: 700;
  }
`;

export const TrackingFlashSale = ({
  flashSaleId,
  flashSaleName,
  status,
}: {
  flashSaleId: string;
  flashSaleName: string;
  status: string;
}) => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const digitDecimal = profile?.company === "ICPL" ? 0 : 2;
  const navigate = useNavigate();
  const [statusRound, setStatusRound] = useState("");
  const [modalProdInCar, setModalProdInCar] = useState(false);
  const [data, setData] = useState<any>();
  const [useRealTime, setUseRealTime] = useState(false);
  const [hasInitialData, setHasInitialData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const { startUploadProcess } = useNotificationContext();

  const fetchFlashSaleTracking = async () => {
    try {
      const response = await getFlashSaleTracking(flashSaleId);
      setStatusRound(response.responseData.viewMode);
      setData(response.responseData);
      if (response.success) setUseRealTime(response.responseData.realtime);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchFlashSaleTracking();
  }, [flashSaleId]);

  const sseUrl = flashSaleSSELiveUrl(flashSaleId);

  const {
    isConnecting,
    isConnected,
    error: sseError,
  } = useSSEFlashSale(sseUrl, {
    enabled: useRealTime,
    onMessage: (data) => {
      if (data.error) {
        console.error("Order SSE Error:", data.error);
        return;
      }
      setData(data);
      setHasInitialData(true);
      setLoading(false);
    },
    onError: (error) => {
      console.error("Order SSE Error:", error);
      setLoading(false);
    },
    onOpen: () => {
      console.log("✅ Order SSE Connected");
    },
  });

  const checkCountDown = (value: any) => {
    const target = new Date(value).getTime();
    const diff = target - Date.now();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    const time = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
    return days > 0 ? "D [วัน] HH:mm:ss" : "HH:mm:ss";
  };
  const exportMutiExcel = async () => {
    setIsExporting(true);
    try {
      const config = await exportFlashSale(flashSaleId);
      const blob = new Blob([config], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      startUploadProcess(
        "",
        progressValue,
        setProgressValue,
        {
          placement: "bottomRight",
          duration: 2,
          label: "กำลังเตรียมไฟล์ของคุณ รอสักครู่!",
          progressInterval: 900,
        },
        async () => {
          try {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `flash_sale_${flashSaleName}-${new Date()
              .toLocaleDateString("us-EN", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
              .replace(/\//g, "-")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(a.href);
            // formatFileSize(blob.size);
          } catch (error) {
            message.error("Export Excel ไม่สำเร็จ");
          } finally {
            setIsExporting(false);
          }
        },
      );
    } catch (e: any) {
      console.log(e);
    }
  };

  const dataInfo = [
    {
      label: "จองอยู่ในตะกร้า (หน่วย)",
      description: `${data?.summary?.cart?.shopCount ?? 0} ร้าน •  มูลค่า ${numberFormatter(data?.summary?.cart?.value ?? 0, 2)} ฿`,
      value: numberFormatter(data?.summary?.cart?.reservedUnits ?? 0, digitDecimal),
      percentage: null,
      bgColor: "#FFF8F5",
      hidden: statusRound !== "LIVE" ? true : false,
    },
    {
      label: "จำนวนที่ขายได้ (หน่วย)",
      description: "ออเดอร์ที่ร้านยืนยันคำสั่งซื้อ",
      value: numberFormatter(data?.summary?.soldUnits ?? 0, digitDecimal),
      percentage: null,
      bgColor: null,
      hidden: false,
    },
    {
      label: "ออเดอร์ทั้งหมด",
      description: "รวมทุกสถานะ",
      value: data?.summary?.orderCount ?? 0,
      percentage: null,
      bgColor: null,
      hidden: false,
    },
    {
      label: "ร้านที่ซื้อ",
      description: `จาก ${data?.summary?.totalShopCount ?? 0} ร้าน`,
      value: data?.summary?.buyingShopCount ?? 0,
      percentage: `${numberFormatter(data?.summary?.buyingShopPercent ?? 0, 2)}%`,
      bgColor: null,
      hidden: false,
    },
  ];

  return (
    <>
      {status === "DRAFT" || status === "WAIT_APPROVE" ? (
        <Card
          bodyStyle={{ backgroundColor: "#F3F6F9", borderRadius: "12px" }}
          style={{ border: "none" }}
        >
          <FlexCol style={{ alignItems: "center" }}>
            <Text level={3} fontWeight={600}>
              แคมเปญ Flash Sale อยู่ในสถานะ {status === "DRAFT" ? `“แบบร่าง”` : `“รออนุมัติ”`}
            </Text>
            <Text color='Text3'>กรุณาดำเนินการสร้างและอนุมัติแคมเปญให้เรียบร้อยก่อน</Text>
            <Text color='Text3'>จึงสามารถติดตามแคมเปญได้</Text>
          </FlexCol>
        </Card>
      ) : (
        <>
          {statusRound === "LIVE" ? (
            <StyledCard
              backgroundColor={
                data?.currentRound?.status === "LIVE"
                  ? ["#FAF9F3", "#FFFBE2", "#FFE864"]
                  : data?.currentRound?.status === "COMING"
                    ? ["#FCFCF8", "#FFFDF5", "#FAECD6"]
                    : ["#F3F6F9", "#F3F6F9", "#F3F6F9"]
              }
            >
              <Row justify='space-between' style={{ alignItems: "center" }}>
                <Col span={18}>
                  <Row>
                    <Text level={6}>รอบปัจจุบัน</Text>
                  </Row>
                  <Row gutter={8}>
                    <Col>
                      <Text level={1} fontWeight={700}>
                        รอบที่ {data?.currentRound?.roundNo}
                      </Text>
                    </Col>
                    <Col style={{ paddingTop: "15px" }}>
                      <Tag
                        style={{
                          backgroundColor: mapBgColorRound[data?.currentRound?.status],
                          border: "none",
                        }}
                      >
                        <Text
                          color={mapTextColorRound[data?.currentRound?.status]}
                          level={6}
                          fontWeight={600}
                        >
                          {mapStatusRound[data?.currentRound?.status]}
                        </Text>
                      </Tag>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Text level={2}>
                        {dayjs(data?.currentRound?.startDate)
                          .locale("th")
                          .add(543, "year")
                          .format("DD MMM YYYY (HH:mm)")}{" "}
                        -{" "}
                        {dayjs(data?.currentRound?.endDate)
                          .locale("th")
                          .add(543, "year")
                          .format("DD MMM YYYY (HH:mm)")}
                      </Text>
                    </Col>
                  </Row>
                </Col>
                {data?.statusLabel !== "INACTIVE" && (
                  <Col span={6}>
                    <Row justify='end' gutter={8} style={{ alignItems: "center" }}>
                      <Text level={6}>
                        {data?.currentRound?.status !== "COMING"
                          ? "เวลาที่เหลือของรอบนี้"
                          : "Countdown รอเริ่มแคมเปญ"}
                      </Text>
                    </Row>
                    <Row justify='end' gutter={8} style={{ alignItems: "center" }}>
                      <Card bodyStyle={{ padding: 6 }}>
                        <Row justify='start' gutter={8} style={{ alignItems: "center" }}>
                          <Col>
                            {data?.statusLabel === "ACTIVE" ? (
                              <img
                                src={icon.thor_flash_sale}
                                style={{ width: "19.3px", height: "27.33px" }}
                              />
                            ) : (
                              <ClockCircleOutlined
                                style={{
                                  color: color.warning,
                                  fontSize: "20px",
                                  fontWeight: "500",
                                }}
                              />
                            )}
                          </Col>
                          <Col>
                            <ConfigProvider
                              theme={{
                                token: {
                                  fontSize: 18,
                                  fontFamily: "IBM Plex Sans Thai",
                                },
                              }}
                            >
                              <StyledTimer
                                type='countdown'
                                value={data.currentRound?.countdownTo}
                                format={checkCountDown(data.currentRound?.countdownTo)}
                              />
                            </ConfigProvider>
                          </Col>
                        </Row>
                      </Card>
                    </Row>
                  </Col>
                )}
              </Row>
            </StyledCard>
          ) : (
            <>
              <Row justify='space-between' style={{ alignItems: "center" }}>
                <Col>
                  <Text fontWeight={700} level={3}>
                    ภาพรวมทั้งแคมเปญ
                  </Text>
                  &nbsp;&nbsp;&nbsp;
                  <Text
                    color='primary'
                    level={6}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/PromotionPage/detailFlashSale/tracking/${flashSaleId}/ALL`)
                    }
                  >
                    ดูรายละเอียด <RightOutlined style={{ fontSize: 10, fontWeight: "500" }} />
                  </Text>
                </Col>
                <Col>
                  <Permission permission={["flashSale", "excel"]}>
                    <Button
                      onClick={exportMutiExcel}
                      typeButton='primary'
                      title='Export'
                      icon={
                        <VerticalAlignBottomOutlined style={{ fontSize: 20, fontWeight: "500" }} />
                      }
                    />
                  </Permission>
                </Col>
              </Row>
            </>
          )}
          <br />
          <Card>
            <FlexCol style={{ alignItems: "center" }}>
              <Text color='Text3'>ยอดขายรวม</Text>
              <Text fontSize={40} fontWeight={700}>
                {numberFormatter(data?.summary?.totalSales, 2)} ฿
              </Text>
              <Text color='Text3'>ออเดอร์ที่ร้านยืนยันคำสั่งซื้อ</Text>
            </FlexCol>
          </Card>
          <br />
          <Row justify='space-between' gutter={16}>
            {dataInfo.map((item: any, index) => (
              <>
                {!item.hidden && (
                  <Col span={24 / dataInfo.filter((item: any) => !item.hidden).length} key={index}>
                    <Card bodyStyle={{ padding: 12 }} style={{ backgroundColor: item.bgColor }}>
                      <FlexCol>
                        <Text color='Text3'>{item.label}</Text>
                        <Text
                          fontSize={40}
                          fontWeight={700}
                          color={index === 0 && item.value > 0 ? "warning" : "BK"}
                        >
                          {item.value}{" "}
                          {index === 0 && (
                            <InfoCircleOutlined
                              onClick={() => setModalProdInCar(true)}
                              style={{
                                fontSize: "18px",
                                fontWeight: "500",
                                cursor: "pointer",
                                color: color.BK,
                              }}
                            />
                          )}
                          {item.percentage && (
                            <Text color='primary' fontWeight={700}>
                              {item.percentage}
                            </Text>
                          )}
                        </Text>
                        <Text
                          color={index === 0 || index === 3 ? "BK" : "Text3"}
                          fontWeight={index === 0 || index === 3 ? 700 : 400}
                        >
                          {item.description}
                        </Text>
                      </FlexCol>
                    </Card>
                  </Col>
                )}
              </>
            ))}
          </Row>
          <br />
          {isExporting && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(128, 128, 128, 0.18)",
                cursor: "not-allowed",
                zIndex: 999,
              }}
            />
          )}
          {statusRound === "LIVE" && (
            <SectionProductQty data={data?.products} company={profile?.company} />
          )}
          <SectionRounds
            flashSaleId={flashSaleId}
            data={data?.rounds}
            currundRound={data?.currentRound}
          />
          {modalProdInCar && (
            <ModalProdInCar
              modalProdInCar={modalProdInCar}
              setModalProdInCar={setModalProdInCar}
              flashSaleRoundId={data?.currentRound?.flashSaleRoundId}
              company={profile?.company}
            />
          )}
        </>
      )}
    </>
  );
};

const ModalProdInCar = ({
  modalProdInCar,
  setModalProdInCar,
  flashSaleRoundId,
  company,
}: {
  modalProdInCar: boolean;
  setModalProdInCar: (modalProdInCar: boolean) => void;
  flashSaleRoundId: string;
  company: string;
}) => {
  const digitDecimal = company === "ICPL" ? 0 : 2;
  const [data, setData] = useState<any>();
  const [searchData, setSearchData] = useState<any>();
  const [searchText, setSearchText] = useState<string>("");
  const [searchZone, setSearchZone] = useState<string>("");
  const [searchCustomerGroup, setSearchCustomerGroup] = useState<string>("");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const fetchData = async () => {
    const result = await getFlashSaleTrackingCart({ flashSaleRoundId: flashSaleRoundId });
    setSearchData(result.responseData.data);
    setData(result.responseData);
  };

  useEffectOnce(() => {
    fetchData();
  });

  const column = [
    {
      title: "ชื่อร้านค้า",
      dataIndex: "customerName",
      key: "customerName",
      width: "50%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text color='Text3' level={6}>
                {row.customerNo} • เขต {row.zone} • กลุ่ม {row.customerGroupName || "-"}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    { title: "จำนวนสินค้า", dataIndex: "productCount", key: "productCount", width: "12%" },
    {
      title: "หน่วยที่จอง",
      dataIndex: "reservedUnits",
      key: "reservedUnits",
      width: "20%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{`${numberFormatter(value, digitDecimal) || "-"}`}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "มูลค่า (฿)",
      dataIndex: "value",
      key: "value",
      width: "20%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{`${numberFormatter(value, 2) || "-"}`}</Text>
            </FlexCol>
          ),
        };
      },
    },
  ];

  const handleSearch = (text: string, zone: string, group: string) => {
    const filteredData = data?.data?.filter((item: any) => {
      return (
        (text ? item.customerNo.includes(text) || item.customerName.includes(text) : true) &&
        (zone ? item.zone.includes(zone) : true) &&
        (group ? item.customerGroupName === group : true)
      );
    });
    setSearchData(filteredData);
  };

  return (
    <Modal
      open={modalProdInCar}
      maskClosable={false}
      onCancel={() => setModalProdInCar(false)}
      footer={null}
      width={1100}
      centered
    >
      <Text level={3} fontWeight={600}>
        รายชื่อร้านค้าที่จองสินค้าในตะกร้า
      </Text>
      <br />
      <Text fontWeight={600}>
        รอบที่ {data?.roundNo} • จำนวนที่จอง {numberFormatter(data?.summary?.reservedUnits, 2)}{" "}
        หน่วย • มูลค่า {numberFormatter(data?.summary?.value, 2)} ฿
      </Text>
      <br />
      <Text>
        อัปเดตล่าสุด{" "}
        {dayjs(data?.summary?.fetchedAt)
          .locale("th")
          .add(543, "year")
          .format("DD MMM YYYY, HH:mm:ss")}
      </Text>
      <Divider size='small' />
      <Row justify='start' gutter={16} style={{ alignItems: "center" }}>
        <Col span={8}>
          <Input
            placeholder='ค้นหารหัส / ชื่อร้านค้า'
            style={{ width: "100%", height: "40px" }}
            onChange={(e) => {
              setSearchText(e.target.value);
              handleSearch(e.target.value, searchZone, searchCustomerGroup);
            }}
          />
        </Col>
        <Col span={4}>
          <Select
            allowClear
            style={{ width: "100%", height: "40px" }}
            placeholder='เขตทั้งหมด'
            options={data?.filterOptions?.zones.map((zone: any) => ({
              label: zone,
              value: zone,
              key: zone,
            }))}
            onChange={(value) => {
              setSearchZone(value);
              handleSearch(searchText, value, searchCustomerGroup);
            }}
          />
        </Col>
        <Col span={6}>
          <Select
            allowClear
            style={{ width: "100%", height: "40px" }}
            placeholder='กลุ่มลูกค้าทั้งหมด'
            options={data?.filterOptions?.customerGroupNames.map((group: any) => ({
              label: group,
              value: group,
              key: group,
            }))}
            onChange={(value) => {
              setSearchCustomerGroup(value);
              handleSearch(searchText, searchZone, value);
            }}
          />
        </Col>
        <Col span={6} style={{ display: "flex", justifyContent: "flex-end" }}>
          <Text>จำนวน {data?.count} ร้าน</Text>
        </Col>
      </Row>
      <br />
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: "#F3F6F9",
              headerColor: "black",
              fontFamily: "IBM Plex Sans Thai",
              fontSize: 15,
              //rowHoverBg: "none",
            },
          },
        }}
      >
        <Table
          rowKey='customerCompanyId'
          columns={column}
          dataSource={searchData}
          pagination={false}
          scroll={{ y: 400 }}
          size='small'
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys: any) => {
              setExpandedRowKeys(keys);
            },

            expandedRowRender: (record: any) => {
              return (
                <Table
                  showHeader={false}
                  columns={[
                    {
                      dataIndex: "productName",
                      key: "productName",
                      width: "61%",
                      render: (value: any, record: any) => (
                        <Row>
                          <div style={{ marginRight: 16 }}>
                            <Avatar
                              src={
                                record.productImage === "No" ||
                                record.productImage === "" ||
                                record.productImage === null
                                  ? image.product_no_image
                                  : record.productImage
                              }
                              size={50}
                              shape='square'
                              onError={() => false}
                            />
                          </div>

                          <FlexCol>
                            <div style={{ height: 25 }}>
                              <Text level={5}>{record.productName}</Text>
                            </div>

                            <div style={{ height: 25 }}>
                              <Text level={5} color='Text3'>
                                {record.packSize}
                              </Text>
                            </div>
                          </FlexCol>
                        </Row>
                      ),
                    },
                    {
                      dataIndex: "quantity",
                      key: "quantity",
                      width: "19.5%",
                      render: (value: any, record: any) => (
                        <FlexCol>
                          <Text>{numberFormatter(value, digitDecimal)}</Text>
                          <Text color='Text3'>{record.saleUomTh}</Text>
                        </FlexCol>
                      ),
                    },
                    {
                      dataIndex: "totalPrice",
                      key: "totalPrice",
                      render: (value: any) => <Text>{numberFormatter(value, 2)}</Text>,
                    },
                  ]}
                  dataSource={record?.items}
                  pagination={false}
                  size='small'
                />
              );
            },

            expandIcon: ({
              expanded,
              onExpand,
              record,
            }: {
              expanded: boolean;
              onExpand: any;
              record: any;
            }) => {
              return expanded ? (
                <UpOutlined onClick={(event) => onExpand(record, event)} />
              ) : (
                <DownOutlined onClick={(event) => onExpand(record, event)} />
              );
            },
          }}
        />
      </ConfigProvider>
    </Modal>
  );
};

const SectionProductQty = ({ data, company }: { data: any; company: string }) => {
  const digitDecimal = company === "ICPL" ? 0 : 2;
  return (
    <>
      <Card bodyStyle={{ padding: 12 }}>
        <div style={{ padding: 16 }}>
          <Text fontWeight={700} level={3}>
            จำนวนสินค้าที่ขายได้ (หน่วย)
          </Text>
        </div>
        <div style={{ maxHeight: 400, overflowY: "auto", padding: 16 }}>
          {data?.map((item, index) => (
            <React.Fragment key={index}>
              <Row justify='space-between' key={index}>
                <Col>
                  <Text fontWeight={600}>{item?.productName}</Text> &nbsp;
                  <Text color='Text3'>({item?.packSize})</Text>&nbsp;
                  <Tag color='#EFF2F9'>
                    <Text level={6} fontWeight={700}>
                      {mapQuotaType[item.quotaType]}
                    </Text>
                  </Tag>
                </Col>
                <Col>
                  <Text>
                    <b>{numberFormatter(item.soldUnits, digitDecimal)}</b>{" "}
                    {item.quotaAmount && `/ ${numberFormatter(item.quotaAmount, digitDecimal)} `}
                    <Text color='Text3'>{item.saleUomTh} </Text>
                    {item.quotaType !== "QUOTA_NONE" &&
                      (item.soldPercent > 100 ? `(100  %)` : `(${item.soldPercent} %)`)}
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Progress
                    percent={item.soldPercent > 100 ? 100 : item.soldPercent}
                    showInfo={false}
                  />
                </Col>
              </Row>
              <Divider size='small' />
            </React.Fragment>
          ))}
        </div>
      </Card>
    </>
  );
};

const SectionRounds = ({
  flashSaleId,
  data,
  currundRound,
}: {
  flashSaleId: string;
  data: any[];
  currundRound: any;
}) => {
  const navigate = useNavigate();
  return (
    <>
      <div style={{ padding: 16 }}>
        <Text fontWeight={700} level={3}>
          รายละเอียดตามรอบ (Breakdown)
        </Text>
      </div>
      <Row justify='space-between' gutter={[16, 16]}>
        {data?.map((item: any, index) => {
          const isCurrentRound = currundRound ? item.roundNo <= currundRound?.roundNo : true;
          return (
            <Col
              key={index}
              flex='1'
              style={{
                minWidth: 0,
              }}
            >
              <Card
                key={index}
                bodyStyle={{ padding: 12 }}
                style={{
                  width: "100%",
                  backgroundColor: item.bgColor,
                  borderRadius: "12px",
                  alignItems: "center",
                }}
              >
                <FlexCol style={{ gap: "6px" }}>
                  <Row>
                    <Col>
                      <Text fontSize={24} fontWeight={700}>
                        รอบที่ {item.roundNo}
                      </Text>
                    </Col>
                    &nbsp;
                    <Col style={{ paddingTop: "6px" }}>
                      <Tag color={mapBgColorRound[!isCurrentRound ? "WAIT" : item?.status]}>
                        <Text
                          color={mapTextColorRound[!isCurrentRound ? "WAIT" : item?.status]}
                          level={6}
                          fontWeight={600}
                        >
                          {mapStatusRound[!isCurrentRound ? "WAIT" : item?.status]}
                        </Text>
                      </Tag>
                    </Col>
                  </Row>
                  <Text level={6}>
                    {dayjs(item?.startDate)
                      .locale("th")
                      .add(543, "year")
                      .format("DD MMM YYYY (HH:mm)")}
                    -
                    {dayjs(item?.endDate)
                      .locale("th")
                      .add(543, "year")
                      .format("DD MMM YYYY (HH:mm)")}
                  </Text>
                  <Text level={1} fontWeight={700}>
                    {numberFormatter(item?.totalSales, 2)} ฿
                  </Text>
                  <Divider size='small' />
                </FlexCol>
                <Row
                  justify='space-between'
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate(
                      `/PromotionPage/detailFlashSale/tracking/${flashSaleId}/${item.flashSaleRoundId}`,
                    );
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  <Col>
                    <Text color='Text3' level={6}>
                      ดูรายละเอียด
                    </Text>
                  </Col>
                  <Col>
                    <RightOutlined style={{ color: color.Text3 }} />
                  </Col>
                </Row>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
};
